/* eslint-disable react/jsx-no-useless-fragment */
import { useEffect, useState } from 'react';

import {
  CaretDownIcon,
  DividerHorizontalIcon,
  FontBoldIcon,
  FontItalicIcon,
  PlusIcon,
  QuoteIcon,
  StrikethroughIcon,
  UnderlineIcon,
} from '@radix-ui/react-icons';
import { useEditorState, type Editor } from '@tiptap/react';

import { Button } from '../../../../primitives/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../../primitives/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../primitives/popover';

import { ListNotionItem } from '../../../list/list-notion-item';

import { cn } from '../../../../utils/cn';
import { Values } from '../../../../utils/types';
import { EDITOR_CONTROLS, type EditorControl } from '../../controls';
import { type List } from './list.type';

// The `CommandInput` uses this value to filter
const COMMAND_ID = {
  bold: 'Negrita bold',
  italic: 'Cursiva italic',
  strike: 'Tachado strike',
  underline: 'Subrayado underline',
  quote: 'Cita quote',
  divider: 'Divisor divider',
} as const;

const COMMANDS: List<typeof COMMAND_ID>[] = [
  {
    id: COMMAND_ID.bold,
    title: 'Negrita',
    description: 'Señala un texto importante.',
    icon: <FontBoldIcon />,
    shortcut: (
      <>
        <span>⌘</span>
        <span className="inline-block w-3 text-right">B</span>
      </>
    ),
    command: (editor: Editor) => editor.chain().focus().toggleBold().run(),
  },
  {
    id: COMMAND_ID.italic,
    title: 'Cursiva',
    description: 'Resalta un texto.',
    icon: <FontItalicIcon />,
    shortcut: (
      <>
        <span>⌘</span>
        <span className="inline-block w-3 text-right">I</span>
      </>
    ),
    command: (editor: Editor) => editor.chain().focus().toggleItalic().run(),
  },
  {
    id: COMMAND_ID.strike,
    title: 'Tachado',
    description: 'Tacha un texto.',
    icon: <StrikethroughIcon />,
    shortcut: (
      <>
        <span>⌘⇧</span>
        <span className="inline-block w-3 text-right">S</span>
      </>
    ),
    command: (editor: Editor) => editor.chain().focus().toggleStrike().run(),
  },
  {
    id: COMMAND_ID.underline,
    title: 'Subrayado',
    description: 'Subraya un texto.',
    icon: <UnderlineIcon />,
    shortcut: (
      <>
        <span>⌘</span>
        <span className="inline-block w-3 text-right">U</span>
      </>
    ),
    command: (editor: Editor) => editor.chain().focus().toggleUnderline().run(),
  },
  {
    id: COMMAND_ID.quote,
    title: 'Cita',
    description: 'Señala una cita.',
    icon: <QuoteIcon />,
    shortcut: (
      <>
        <span>⌘⇧</span>
        <span className="inline-block w-3 text-right">B</span>
      </>
    ),
    command: (editor: Editor) => editor.chain().focus().toggleBlockquote().run(),
  },
  {
    id: COMMAND_ID.divider,
    title: 'Divisor',
    description: 'Separa los bloques visualmente.',
    icon: <DividerHorizontalIcon />,
    command: (editor: Editor) => editor.chain().focus().setHorizontalRule().run(),
  },
];

const COMMAND_CONTROLS: Record<Values<typeof COMMAND_ID>, EditorControl> = {
  [COMMAND_ID.bold]: EDITOR_CONTROLS.toggleBold,
  [COMMAND_ID.italic]: EDITOR_CONTROLS.toggleItalic,
  [COMMAND_ID.strike]: EDITOR_CONTROLS.toggleStrike,
  [COMMAND_ID.underline]: EDITOR_CONTROLS.toggleUnderline,
  [COMMAND_ID.quote]: EDITOR_CONTROLS.toggleQuote,
  [COMMAND_ID.divider]: EDITOR_CONTROLS.buttonDivider,
};

interface ComboboxCommandsProps {
  controls?: EditorControl[];
  editor: Editor;
}

export function ComboboxCommands({ controls, editor }: ComboboxCommandsProps) {
  const [open, setOpen] = useState(false);
  const [option, setOption] = useState<Values<typeof COMMAND_ID>[]>([]); // several of these commands can be executed at the same time.

  const editorState = useEditorState({
    editor,
    selector: ({ editor }: { editor: Editor }) => ({
      isBold: editor.isActive('bold'),
      isItalic: editor.isActive('italic'),
      isStrike: editor.isActive('strike'),
      isUnderline: editor.isActive('underline'),
      isBlockquote: editor.isActive('blockquote'),
    }),
  });

  useEffect(() => {
    const activeCommands: Values<typeof COMMAND_ID>[] = [];

    if (editorState.isBold) {
      activeCommands.push(COMMAND_ID.bold);
    }
    if (editorState.isItalic) {
      activeCommands.push(COMMAND_ID.italic);
    }
    if (editorState.isStrike) {
      activeCommands.push(COMMAND_ID.strike);
    }
    if (editorState.isUnderline) {
      activeCommands.push(COMMAND_ID.underline);
    }
    if (editorState.isBlockquote) {
      activeCommands.push(COMMAND_ID.quote);
    }

    setOption(activeCommands);
  }, [editorState]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={!editor.isEditable}>
        <Button
          aria-label="Opciones de formato de documento y texto"
          title="Opciones de formato de documento y texto"
          className="w-14 justify-between gap-0 rounded-full px-2"
          role="combobox"
          variant="ghost"
        >
          <PlusIcon className="size-4" />
          <CaretDownIcon className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command defaultValue={option[0]}>
          <CommandInput placeholder="Buscar" />
          <CommandList className="max-h-[12.5rem]">
            <CommandEmpty>Sin resultados 🫥</CommandEmpty>
            <CommandGroup>
              {COMMANDS.filter(
                ({ id }) => !controls || controls.includes(COMMAND_CONTROLS[id]),
              ).map(({ id, title, description, icon, shortcut, command }) => (
                <CommandItem
                  className={cn('group mb-1 last:mb-0', {
                    'bg-accent/50 text-accent-foreground': option.includes(id),
                  })}
                  key={id}
                  value={id}
                  onSelect={() => {
                    command(editor);
                    setOpen(false);
                  }}
                >
                  <ListNotionItem
                    title={title}
                    description={description}
                    icon={icon}
                    shortcut={shortcut}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
